<template>
  <div class="header-search-container">
    <b-form
      novalidate
      class="header-search-wrapper"
      :class="{ 'header-search--expanded': isExpanded }"
      @submit.prevent="search"
    >
        <b-input-group class="nav-search-input-group">
          <b-form-input
            ref="searchInput"
            v-model="params.search"
            class="nav-search-input"
            placeholder="Search for a service or session"
            @touchstart="focusInput"
            @focus="handleFocus"
            @blur="handleBlur"
          />
          <b-input-group-append>
            <button
              @click="openFilterSidebar"
              class="filter-btn"
              type="button"
              aria-label="Open filter menu"
            >
              <FilterFcs
                class="filter-icon"
                aria-label="filter icon"
              />
            </button>
            <button
              @click="search"
              class="btn btn-primary"
              type="button"
            >
              <SearchIcon/>
            </button>
          </b-input-group-append>
        </b-input-group>
    </b-form>

  </div>
</template>

<script>
import { SearchIcon, XIcon } from "@vue-hero-icons/outline"
import SearchGroupMobile from '@/modules/results/components/Search/SearchGroupMobile'
import SearchFiltersMobile from '@/modules/results/components/SearchFiltersMobile'
import FilterFcs from '@/assets/icons/filter-fcs.svg'

export default {
  inject: {
    handleSearchExpanded: {
      from: 'handleSearchExpanded',
      default: null
    }
  },

  components: {
    SearchIcon,
    FilterFcs
  },

  data() {
    return {
      params: {
    		search: this.$route.query.search,
    	},
      isExpanded: false
    }
  },

  watch: {

    'params.search'(newVal) {
      if (!newVal || newVal.trim() === '') {
        this.isExpanded = false
        if (this.handleSearchExpanded) {
          this.handleSearchExpanded(false)
        }
      }
    }
  },

  methods: {
    search() {
      this.$emit('search', this.params)
      this.isExpanded = false
      if (this.handleSearchExpanded) {
        this.handleSearchExpanded(false)
      }
    },

    focusInput() {
      // iOS Safari: sticky navbar inputs sometimes need explicit focus on touchstart
      const el = this.$refs.searchInput && (this.$refs.searchInput.$el || this.$refs.searchInput)
      if (el && document.activeElement !== el) {
        el.focus()
      }
    },

    handleFocus() {
      this.isExpanded = true
      if (this.handleSearchExpanded) {
        this.handleSearchExpanded(true)
      }
    },

    handleBlur() {
      // Use setTimeout to check value after blur completes
      setTimeout(() => {
        if (!this.params.search || this.params.search.trim() === '') {
          this.isExpanded = false
          if (this.handleSearchExpanded) {
            this.handleSearchExpanded(false)
          }
        }
      }, 150)
    },

    openFilterSidebar() {
      if (this.$root.$filterSidebar) {
        this.$root.$filterSidebar.openFilterSidebar()
      }
    }
  }
}
</script>

<style lang="scss">
  // Note: Filter sidebar z-index is handled in _global-layout.scss
  // since Bootstrap Vue renders b-sidebar at body level

  .header-search-container {
    width: 100%;
    display: flex;
    align-items: center;
    height: 100%;
  }

  .header-search-wrapper {
    // Form element needs to fill parent and be flexible
    width: 100%;
    display: flex;
    align-items: center;
    transition: max-width 0.3s ease;
    flex: 1 1 auto;
    min-width: 0;
    box-sizing: border-box;

    @media (max-width: 576px) {
      min-width: 0;
      max-width: 100%;
      flex: 1 1 auto;
    }

    // Always flexible to fill available space on all screen sizes
    @media (min-width: 768px) {
      width: 100%;
      flex: 1 1 auto;
      min-width: 0;
    }

    &.header-search--expanded {
      @media (max-width: 576px) {
        // max-width controlled by parent .navbar-search — no override needed here
      }

      // iPad and desktop: grow the input visually when focused
      @media (min-width: 768px) {
        flex: 2 1 auto !important;
      }
    }
  }

  .nav-search-input-group {
    // This class is on the input-group div itself
    // Bootstrap's input-group already uses display: flex
    width: 100%;
    flex: 1 1 auto;
    min-width: 0;
    flex-wrap: nowrap !important;

    // Don't override Bootstrap's form-control width: 1% - it's the flex trick
    // Just ensure it can shrink properly and has consistent height
    .form-control {
      min-width: 80px;
      height: 38px; // Match button heights
      border-color: #5A5A5A;
      touch-action: manipulation; // Remove iOS 300ms tap delay so keyboard appears immediately

      &:focus {
        outline: none;
        box-shadow: none;
      }
    }

    // Ensure buttons stay inline and never stack
    .input-group-append {
      flex-shrink: 0;
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
    }

    .filter-btn {
      // Set all borders explicitly to match form-control
      border-top: 1px solid #ced4da !important;
      border-bottom: 1px solid #ced4da !important;
      border-left: 1px solid #ced4da !important;
      border-right: none !important;
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
      background-color: transparent !important;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.375rem 0.75rem;
      flex-shrink: 0;
      height: 38px; // Match form-control height
      cursor: pointer;
      
      &:hover {
        border-top-color: #ced4da !important;
        border-bottom-color: #ced4da !important;
        border-left-color: #ced4da !important;
        background-color: transparent !important;
      }
      
      &:focus {
        border-top-color: #ced4da !important;
        border-bottom-color: #ced4da !important;
        border-left-color: #ced4da !important;
        box-shadow: 0 0 0 0.2rem rgba($primary, 0.25) !important;
        outline: none;
      }
      
      &:active,
      &:focus:active {
        border-top-color: #adb5bd !important;
        border-bottom-color: #adb5bd !important;
        border-left-color: #adb5bd !important;
        background-color: transparent !important;
        box-shadow: none !important;
      }
      
      .filter-icon {
        width: 20px;
        height: 20px;
        display: inline-block;
        
        // Target the SVG component and override hardcoded stroke colors
        // Use exact hex value to ensure it works
        ::v-deep svg {
          display: block;
          width: 100%;
          height: 100%;
          
          // Override the rect border (the circle around the icon) - make it transparent
          rect {
            stroke: transparent !important;
            fill: transparent !important;
          }
          
          // Override the path stroke (the actual filter icon) - use exact hex color
          path {
            stroke: #A30083 !important;
            fill: none !important;
          }
          
          // Target all elements with stroke attribute - override inline styles
          * {
            &[stroke="#333F48"],
            &[stroke="#DFDFDF"],
            &[stroke] {
              stroke: #A30083 !important;
            }
            &[fill] {
              fill: none !important;
            }
          }
        }
      }
      
      &:hover .filter-icon {
        ::v-deep svg {
          path {
            stroke: #8F0075 !important; // Darkened primary
          }
          
          *[stroke] {
            stroke: #8F0075 !important;
          }
        }
      }
    }

    .btn-primary {
      border-left: none;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      flex-shrink: 0;
      height: 38px; // Match form-control height
    }
  }
</style>